/**
 * Environment variable validation utilities
 * Ensures proper configuration and prevents runtime errors
 */

export interface EnvConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  environment: 'development' | 'production' | 'test';
}

// Validate URL format
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Validate Supabase URL specifically
const isValidSupabaseUrl = (url: string): boolean => {
  if (!isValidUrl(url)) return false;
  
  // Supabase URLs typically follow the pattern: https://[project-id].supabase.co
  const supabasePattern = /^https:\/\/[a-zA-Z0-9-]+\.supabase\.co$/;
  return supabasePattern.test(url);
};

// Validate API key format (basic length and character validation). Two kinds
// pass: the legacy JWT anon key (long, base64-like) and the publishable key
// that replaces it (sb_publishable_…, ~46 characters). Supabase retires the
// legacy keys at the end of 2026, so a length-only check would refuse the
// replacement and stop the app starting.
const isValidApiKey = (key: string): boolean => {
  if (key.startsWith('sb_publishable_')) {
    return /^sb_publishable_[A-Za-z0-9_-]+$/.test(key);
  }
  return key.length > 100 && /^[A-Za-z0-9._-]+$/.test(key);
};

// Get and validate environment variables
export const validateEnvironment = (): EnvConfig => {
  const errors: string[] = [];
  
  // Get environment variables with fallbacks
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim() || '';
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim() || '';
  const nodeEnv = process.env.NODE_ENV || 'development';
  
  // Validate Supabase URL
  if (!supabaseUrl) {
    errors.push('REACT_APP_SUPABASE_URL is required but not provided');
  } else if (!isValidSupabaseUrl(supabaseUrl)) {
    errors.push('REACT_APP_SUPABASE_URL must be a valid Supabase URL (https://[project-id].supabase.co)');
  }
  
  // Validate Supabase anon key
  if (!supabaseAnonKey) {
    errors.push('REACT_APP_SUPABASE_ANON_KEY is required but not provided');
  } else if (supabaseAnonKey.startsWith('sb_secret_')) {
    // Everything behind REACT_APP_ is shipped to every browser.
    errors.push('REACT_APP_SUPABASE_ANON_KEY holds a secret key; use the publishable key (sb_publishable_…)');
  } else if (!isValidApiKey(supabaseAnonKey)) {
    errors.push('REACT_APP_SUPABASE_ANON_KEY appears to be invalid (check length and format)');
  }
  
  // Validate environment
  const validEnvironments = ['development', 'production', 'test'];
  const environment = validEnvironments.includes(nodeEnv) 
    ? nodeEnv as 'development' | 'production' | 'test'
    : 'development';
  
  // If there are validation errors, provide helpful messages
  if (errors.length > 0) {
    const errorMessage = [
      '❌ Environment Configuration Errors:',
      ...errors.map(error => `  • ${error}`),
      '',
      '🔧 To fix these issues:',
      '  1. Create a .env file in your project root if it doesn\'t exist',
      '  2. Add the following variables:',
      '     REACT_APP_SUPABASE_URL=https://your-project-id.supabase.co',
      '     REACT_APP_SUPABASE_ANON_KEY=your-anon-key-here',
      '  3. Restart your development server',
      '',
      '📚 For more help, check the Supabase documentation or project README'
    ].join('\n');
    
    // In development, show user-friendly error
    if (environment === 'development') {
      throw new Error(errorMessage);
    } else {
      // In production, log error and throw generic message
      console.error('Environment validation failed:', errors);
      throw new Error('Application configuration error. Please check server logs.');
    }
  }
  
  return {
    supabaseUrl,
    supabaseAnonKey,
    environment
  };
};

// Runtime configuration check
export const checkRuntimeConfig = (): boolean => {
  try {
    validateEnvironment();
    return true;
  } catch (error) {
    console.error('Runtime configuration check failed:', error);
    return false;
  }
};

// Environment-specific logging
export const logEnvironmentInfo = (): void => {
  try {
    const config = validateEnvironment();
    console.log(`🚀 Application starting in ${config.environment} mode`);
    console.log(`🔗 Supabase URL: ${config.supabaseUrl.substring(0, 30)}...`);
    console.log(`🔑 API key configured: ${config.supabaseAnonKey ? '✅' : '❌'}`);
  } catch (error) {
    console.error('Failed to log environment info:', error);
  }
};